// OptimizedPagination.jsx
import React, { useState, useEffect, useCallback } from 'react';
import './OptimizedPagination.css';

const OptimizedPagination = ({ 
  totalItems, 
  itemsPerPage, 
  currentPage, 
  onPageChange,
  maxDisplayedPages = 5
}) => {
  const [displayedPages, setDisplayedPages] = useState([]);
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  // Pre-load next page data when close to the end of current page
  useEffect(() => {
    // If we're not on the last page, preload next page
    if (currentPage < totalPages) {
      // You could implement a function here to preload the next page's data
      // This would typically be passed as a prop if needed
      // preloadPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);

  // Calculate which page numbers to display
  const calculateDisplayedPages = useCallback(() => {
    let startPage = Math.max(1, currentPage - Math.floor(maxDisplayedPages / 2));
    let endPage = startPage + maxDisplayedPages - 1;
    
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxDisplayedPages + 1);
    }
    
    const pages = [];
    
    // Always include first page
    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) {
        pages.push('ellipsis-start');
      }
    }
    
    // Add pages in the middle
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    // Always include last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push('ellipsis-end');
      }
      pages.push(totalPages);
    }
    
    return pages;
  }, [currentPage, totalPages, maxDisplayedPages]);
  
  // Update displayed pages when dependencies change
  useEffect(() => {
    setDisplayedPages(calculateDisplayedPages());
  }, [calculateDisplayedPages, currentPage, totalPages]);
  
  // Handle page click with debouncing to prevent rapid multiple clicks
  const handlePageClick = useCallback((page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      onPageChange(page);

      // Scroll to top of products section with smooth animation
      const productsSection = document.querySelector('.products-section');
      if (productsSection) {
        productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // Fallback to scrolling to top of page
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [currentPage, totalPages, onPageChange]);
  
  if (totalPages <= 1) return null;
  
  return (
    <div className="optimized-pagination">
      <button 
        className="pagination-arrow"
        onClick={() => handlePageClick(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Previous page"
      >
        &laquo; Prev
      </button>
      
      <div className="pagination-numbers">
        {displayedPages.map((page, index) => (
          page === 'ellipsis-start' || page === 'ellipsis-end' ? (
            <span key={page} className="pagination-ellipsis">...</span>
          ) : (
            <button
              key={page}
              onClick={() => handlePageClick(page)}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              aria-current={currentPage === page ? 'page' : undefined}
              aria-label={`Page ${page}`}
            >
              {page}
            </button>
          )
        ))}
      </div>
      
      <button 
        className="pagination-arrow"
        onClick={() => handlePageClick(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Next page"
      >
        Next &raquo;
      </button>
    </div>
  );
};

export default OptimizedPagination;